// ---------------------------------------------------------------------------
// V28 — THE ABSENCE LADDER, THE APPEAL QUEUE, AND A WORKER'S OWN RECORD.
//
// Three screens in one file because they are three views of one subject and the
// vocabulary loader, the tone narrowing and the factor table are shared. Split
// across three files each would carry its own copy and the copies would drift —
// which is what happened to `READ_ONLY_ROUTES` across five verify scripts.
//
// ---------------------------------------------------------------------------
// NOTHING HERE COMPUTES A TIME OR A LATENESS
//
// `minutes_overdue`, `call_at` and `evaluated_at` all arrive from the server,
// derived in SQL from the call time. The browser's clock belongs to the person
// reading the screen, and on this screen that person is sometimes the person the
// record is about.
//
// ---------------------------------------------------------------------------
// A REFUSED READ NEVER RENDERS AS AN EMPTY ONE
//
// V27's gallery found a cell reading *Nobody is held in reserve* after the read
// had been REFUSED — a confident statement of fact about a question the screen
// had been told it may not ask, indistinguishable from the truth. An appeal
// route must never do that: *you have nothing to appeal* addressed to somebody
// whose record could not be read is exactly the state Directive 2024/2831 makes
// illegal, wearing the appearance of everything working.
//
// So every list on these screens distinguishes NULL (not read, or refused) from
// an empty array, and the failed case says so in words.
//
// NO NEW CSS. `stack`, `panel`, `data-table`, `field`, `btn`, `muted`, `numeric`
// all already carry rules in `web/src/design`.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import {
  reliability as api,
  type Appeal,
  type AppealGround,
  type AppealableShift,
  type Escalation,
  type Reliability,
} from "../api/reliability";
import { Notice } from "../design/Notice";
import { StatusChip } from "../design/StatusChip";
import { TONES, type Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { factorSentence } from "../i18n/record";
import { intlTag, translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

type Outcome = { id: string; label: string; tone: string; sets_aside: boolean; description: string };

/**
 * The server's tone, narrowed to the four this design system has.
 *
 * A TONE THAT IS NOT ONE OF THE FOUR FALLS BACK TO `neutral` RATHER THAN
 * THROWING. `statusChip` throws on an unknown tone, and a fifth stage added to
 * `app.attendance_escalation_stage` next year must not take a whole screen down
 * — the row still carries its LABEL, which is what a reader needs. Status is
 * never carried by colour alone here for exactly that reason.
 */
function toneOf(value: string | null): Tone {
  if (value === null) return "neutral";
  return (TONES as readonly string[]).includes(value) ? (value as Tone) : "neutral";
}

/** The four V28 vocabularies, fetched once. Nothing on these screens spells one. */
function useVocabulary(apiBase: string) {
  const [grounds, setGrounds] = useState<AppealGround[] | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);
  useEffect(() => {
    void (async () => {
      const res = await send<{ appeal_grounds?: AppealGround[]; appeal_outcomes?: Outcome[] }>(
        api.vocabulary(apiBase),
      );
      // NULL ON A REFUSAL AND NOT AN EMPTY LIST. A screen with no grounds to
      // offer must say it could not read them, never present a route with no
      // doors on it.
      setGrounds(res.status === 200 ? (res.body?.appeal_grounds ?? []) : null);
      setOutcomes(res.status === 200 ? (res.body?.appeal_outcomes ?? []) : null);
    })();
  }, [apiBase]);
  return { grounds, outcomes };
}

/**
 * The score as its terms. NEVER AS A BARE NUMBER.
 *
 * `app.worker_reliability` returns one row per factor precisely so that this
 * component cannot be written any other way, and a factor that could not be
 * measured says so instead of contributing a zero.
 */
function FactorTable({ record }: { record: Reliability }) {
  const { t, locale } = useT();
  // A decimal in the reader's notation — "0,85" in Hungarian. English keeps toFixed's point exactly.
  const fixed = (n: number, digits: number) =>
    locale === "en"
      ? n.toFixed(digits)
      : n.toLocaleString(intlTag(locale), { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return (
    <>
      <p>
        {record.score === null ? (
          <StatusChip tone="neutral" label={t("There is not enough history to work out a record yet")} />
        ) : (
          <StatusChip
            tone={record.score >= 0.8 ? "live" : record.score >= 0.5 ? "attention" : "failed"}
            label={t("Record: {score} out of 100, over {shifts} shift(s)", {
              score: Math.round(record.score * 100),
              shifts: record.shifts_counted,
            })}
          />
        )}
      </p>
      {record.shifts_set_aside > 0 ? (
        <p className="muted">
          {t(
            "{count} shift(s) were set aside after an appeal. They count in neither direction — not against the record and not in its favour.",
            { count: record.shifts_set_aside },
          )}
        </p>
      ) : null}
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{t("What was measured")}</th>
            <th scope="col">{t("Weight")}</th>
            <th scope="col">{t("Value")}</th>
            <th scope="col">{t("Contributed")}</th>
            <th scope="col">{t("Why")}</th>
          </tr>
        </thead>
        <tbody>
          {record.factors.map((f) => (
            <tr key={f.factor}>
              <th scope="row">{f.factor_label}</th>
              <td className="numeric">{f.weight === null ? "—" : fixed(f.weight, 2)}</td>
              <td className="numeric">
                {f.is_measurable && f.value !== null ? fixed(f.value, 2) : "—"}
              </td>
              <td className="numeric">
                {f.is_measurable && f.contribution !== null ? fixed(f.contribution, 3) : "—"}
              </td>
              <td>
                {f.is_measurable ? null : (
                  <StatusChip tone="neutral" label={t("Left out — it could not be measured")} />
                )}{" "}
                {/* S74. Worded here from the counts (0218); the database's English `sentence` is the console's. */}
                {factorSentence(locale, f.counts_arrivals, record)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// ===========================================================================
// `console.events.attendance` — WHAT THE LADDER RAISED ON ONE EVENT.
// ===========================================================================
export function EventAttendance() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const params = useParams();
  const eventId = params["id"] ?? "";

  const [rows, setRows] = useState<Escalation[] | null>(null);
  const [mayEscalate, setMayEscalate] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await send<{
      escalations?: Escalation[];
      may_escalate?: boolean;
      error?: { message: string };
    }>(api.eventEscalations(apiBase, eventId));
    if (res.status === 200 && Array.isArray(res.body?.escalations)) {
      setRows(res.body.escalations);
      setMayEscalate(res.body.may_escalate === true);
      setFailed(null);
    } else {
      // NULL, NOT AN EMPTY LIST. "Nobody is late" and "this could not be read"
      // are opposite facts and only one of them is reassuring.
      setRows(null);
      setFailed(refusal(res.body?.error) ?? t("The attendance record for this event could not be read."));
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const escalate = useCallback(async () => {
    setBusy(true);
    const res = await send<{
      escalation?: { late_raised: number; absences_raised: number; reserves_called: number };
      error?: { message: string };
    }>(api.escalate(apiBase));
    setBusy(false);
    if (res.status >= 400 || !res.body?.escalation) {
      setFailed(refusal(res.body?.error) ?? t("The absence ladder could not be run."));
      return;
    }
    const e = res.body.escalation;
    setSaid(
      t("{late} person(s) marked as running late, {absent} place(s) treated as empty, {reserves} reserve(s) called up.", {
        late: e.late_raised,
        absent: e.absences_raised,
        reserves: e.reserves_called,
      }),
    );
    await load();
  }, [apiBase, load]);

  return (
    <div className="stack">
      <h1>{t("Late and absent")}</h1>

      <section className="panel stack">
        <p className="muted">
          {t("Lateness is measured from each post’s call time and from nothing else. A post that records no arrivals at all is never escalated — silence is not absence. When a place is treated as empty a named reserve is called up if one is standing by, and the client is told that a place is being refilled.")}
        </p>
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {said !== null ? <StatusChip tone="live" label={said} /> : null}

        {rows !== null && rows.length === 0 ? (
          <p>{t("Nobody on this event has passed their call time without an arrival being recorded.")}</p>
        ) : null}

        {rows !== null && rows.length > 0 ? (
          <>
            <h2>{t("What was raised")}</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Person")}</th>
                <th scope="col">{t("Stage")}</th>
                <th scope="col">{t("Due at")}</th>
                <th scope="col">{t("Minutes over")}</th>
                <th scope="col">{t("Judged at")}</th>
                <th scope="col">{t("Reserve called up")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">{r.worker_name}</th>
                  <td>
                    <StatusChip tone={toneOf(r.tone)} label={tl(r.stage_label)} />
                  </td>
                  <td>{whenIn(r.call_at, orgZone)}</td>
                  <td className="numeric">{r.minutes_overdue}</td>
                  <td>{whenIn(r.evaluated_at, orgZone)}</td>
                  <td>
                    {r.promoted_worker_name ?? (
                      <span className="muted">{t("Nobody was standing by")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        ) : null}

        {mayEscalate ? (
          <div className="row">
            <button type="button" className="btn" disabled={busy} onClick={() => void escalate()}>
              {t("Run the absence ladder now")}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

// ===========================================================================
// `console.people.appeals` — THE NAMED HUMAN AT THE END OF THE ROUTE.
// ===========================================================================
export function AppealQueue() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { outcomes } = useVocabulary(apiBase);

  const [rows, setRows] = useState<Appeal[] | null>(null);
  const [mayDecide, setMayDecide] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await send<{ appeals?: Appeal[]; may_decide?: boolean; error?: { message: string } }>(
      api.appeals(apiBase),
    );
    if (res.status === 200 && Array.isArray(res.body?.appeals)) {
      setRows(res.body.appeals);
      setMayDecide(res.body.may_decide === true);
      setFailed(null);
    } else {
      setRows(null);
      setFailed(refusal(res.body?.error) ?? t("The appeals could not be read."));
    }
  }, [apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (id: string, outcome: string) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(api.decide(apiBase, id, outcome));
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That appeal could not be decided."));
        return;
      }
      setSaid(t("Decided. The record now names you as the person who decided it."));
      await load();
    },
    [apiBase, load],
  );

  return (
    <div className="stack">
      <h1>{t("Appeals")}</h1>

      <section className="panel stack">
        <p className="muted">
          {t("A reliability record affects who is offered work, which makes it an automated decision about somebody’s livelihood. Every one of them can be contested, and every decision here is recorded against the name of the person who made it. Upholding an appeal removes the shift from the record in both directions.")}
        </p>
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {said !== null ? <StatusChip tone="live" label={said} /> : null}
        {outcomes === null ? (
          <Notice tone="attention">{t("The list of outcomes could not be read, so nothing can be decided from this screen right now")}</Notice>
        ) : null}

        {rows !== null && rows.length === 0 ? <p>{t("Nobody has contested their record.")}</p> : null}

        {rows !== null && rows.length > 0 ? (
          <>
          <h2>{rows.filter((r) => r.outcome === null).length} waiting for a decision</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Person")}</th>
                <th scope="col">{t("Ground")}</th>
                <th scope="col">{t("Opened")}</th>
                <th scope="col">{t("Outcome")}</th>
                <th scope="col">{t("Decided by")}</th>
                <th scope="col">{t("Decide")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">{r.worker_name}</th>
                  <td>{tl(r.ground_label)}</td>
                  <td>{whenIn(r.opened_at, orgZone)}</td>
                  <td>
                    {r.outcome === null ? (
                      <StatusChip tone="attention" label={t("Waiting for a person")} />
                    ) : (
                      <StatusChip tone={toneOf(r.outcome_tone)} label={tl(r.outcome_label ?? r.outcome)} />
                    )}
                  </td>
                  <td>
                    {r.decided_by_name ?? <span className="muted">{t("Nobody yet")}</span>}
                    {r.decided_at === null ? null : ` · ${whenIn(r.decided_at, orgZone)}`}
                  </td>
                  <td>
                    {/* THE CONTROL IS NOT CONDITIONED ON THE MACHINE AGREEING, and it
                        is not hidden in the states the queue finds awkward. It is
                        absent only where there is genuinely nothing left to decide. */}
                    {r.outcome === null && mayDecide
                      ? (outcomes ?? []).map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            className="btn"
                            disabled={busy}
                            title={tl(o.description)}
                            onClick={() => void decide(r.id, o.id)}
                          >
                            {tl(o.label)}
                          </button>
                        ))
                      : null}
                    {r.outcome === null && !mayDecide ? (
                      <span className="muted">{t("Somebody with permission to manage the bench decides this")}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        ) : null}
      </section>
    </div>
  );
}

// ===========================================================================
// `worker.record` — THE ROUTE THE WORKER CAN ACTUALLY REACH.
//
// This screen is the whole reason V28 is not just an escalation feature. A score
// that decides who gets offered work and whose subject cannot see it, understand
// it or contest it is the automated decision Directive 2024/2831 is about.
// ===========================================================================
export function MyRecord() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { grounds } = useVocabulary(apiBase);

  const [record, setRecord] = useState<Reliability | null>(null);
  const [appealable, setAppealable] = useState<AppealableShift[] | null>(null);
  const [appeals, setAppeals] = useState<Appeal[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ground, setGround] = useState("");

  const load = useCallback(async () => {
    const res = await send<{
      reliability?: Reliability;
      appealable?: AppealableShift[];
      appeals?: Appeal[];
      error?: { message: string };
    }>(api.mine(apiBase));
    if (res.status === 200 && res.body?.reliability) {
      setRecord(res.body.reliability);
      setAppealable(res.body.appealable ?? []);
      setAppeals(res.body.appeals ?? []);
      setFailed(null);
    } else {
      // ALL THREE TO NULL TOGETHER. Leaving `appealable` as an empty array here
      // is how a worker is told "you have nothing to appeal" when what actually
      // happened is that nothing could be read.
      setRecord(null);
      setAppealable(null);
      setAppeals(null);
      setFailed(refusal(res.body?.error) ?? t("Your record could not be read just now."));
    }
  }, [apiBase, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const raise = useCallback(
    async (assignmentId: string) => {
      if (ground === "") {
        setFailed(t("Choose a ground first, so the person reading it knows what you are contesting."));
        return;
      }
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        api.appeal(apiBase, { assignment_id: assignmentId, ground }),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That appeal could not be opened."));
        return;
      }
      setSaid(t("Sent. A person will decide it and you will be told the outcome and who decided it."));
      await load();
    },
    [apiBase, ground, load, t],
  );

  return (
    <div className="stack">
      <h1>{t("My record")}</h1>

      <section className="panel">
        <p className="muted">
          {t(
            "This is what the agency’s system knows about your attendance, and it is used when work is being offered. Every part of it is shown below with the reason for it. If any of it is wrong you can say so, and a person — not the system — decides.",
          )}
        </p>
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {said !== null ? <StatusChip tone="live" label={said} /> : null}
      </section>

      {record !== null ? (
        <section className="panel">
          <h2>{t("How it is worked out")}</h2>
          <FactorTable record={record} />
        </section>
      ) : null}

      {/* THE APPEAL FORM IS PRESENT WHENEVER THE RECORD IS. It is not conditioned
          on there being something to appeal, because a route that appears only
          once you already have a problem is a route nobody knows exists. */}
      {record !== null ? (
        <section className="panel">
          <h2>{t("Something here is wrong")}</h2>
          {grounds === null ? (
            <Notice tone="attention">{t("The list of grounds could not be read, so this form cannot be used right now. It is not that you have no route — please ask the office.")}</Notice>
          ) : (
            <>
              <div className="field">
                <label htmlFor="v28-ground">{t("What is wrong")}</label>
                <select id="v28-ground" value={ground} onChange={(e) => setGround(e.target.value)}>
                  <option value="">{t("Choose one")}</option>
                  {grounds.map((g) => (
                    <option key={g.id} value={g.id} title={g.description}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="muted">
                {t(
                  "We do not ask you to write anything here, and there is nowhere to. If none of these fits, choose the last one and somebody will call you.",
                )}
              </p>
            </>
          )}

          {appealable !== null && appealable.length === 0 ? (
            <p className="muted">
              {t("Nothing currently counts against your record, so there is nothing to contest.")}
            </p>
          ) : null}

          {appealable !== null && appealable.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Shift")}</th>
                  <th scope="col">{t("When")}</th>
                  <th scope="col">{t("What was recorded")}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {appealable.map((s) => (
                  <tr key={s.assignment_id}>
                    <th scope="row">{s.event_name}</th>
                    <td>{whenIn(s.starts_at, orgZone)}</td>
                    <td>{s.stage_label}</td>
                    <td>
                      <button
                        type="button"
                        className="btn"
                        disabled={busy || grounds === null}
                        onClick={() => void raise(s.assignment_id)}
                      >
                        {t("This is wrong")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      ) : null}

      {appeals !== null && appeals.length > 0 ? (
        <section className="panel">
          <h2>{t("What you have contested")}</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Ground")}</th>
                <th scope="col">{t("Opened")}</th>
                <th scope="col">{t("Outcome")}</th>
                <th scope="col">{t("Decided by")}</th>
              </tr>
            </thead>
            <tbody>
              {appeals.map((a) => (
                <tr key={a.id}>
                  <th scope="row">{a.ground_label}</th>
                  <td>{whenIn(a.opened_at, orgZone)}</td>
                  <td>
                    {a.outcome === null ? (
                      <StatusChip tone="attention" label={t("Waiting for a person to decide")} />
                    ) : (
                      <StatusChip tone={toneOf(a.outcome_tone)} label={a.outcome_label ?? a.outcome} />
                    )}
                  </td>
                  {/* S61, S103. /v1/my/reliability says only whether the agency decided (S57); the words are ours. */}
                  <td>{a.decided_by_agency ? t("Your agency") : <span className="muted">{t("Nobody yet")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
